import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { HdBodygraphPlanetaryActivation, HdBodygraphRenderState } from '../hd-bodygraph.models';
import { HD_BODYGRAPH_COLORS } from '../hd-bodygraph-metadata';
import { HdChartHoverHighlightService } from '../hd-chart-hover-highlight.service';
import { buildHdGateBlockClusters, buildHdGateBlockDescriptors } from './hd-gate-block-descriptors';
import {
  buildHdGateBlockChannelSegments,
  clampHdGateBlockRectToViewBox,
  computeHdGateBlockLayout,
  snapHdGateBlockRect,
} from './hd-gate-block-layout';
import { HD_GATE_BLOCK_DRAG_THRESHOLD, HdGateBlockPin, HdGateBlockRect, HdGateBlockSide, hdGateBlockKey } from './hd-gate-block.models';

interface GateBlockViewModel {
  readonly key: string;
  readonly gate: number;
  readonly planetId: number;
  readonly side: HdGateBlockSide;
  readonly rect: HdGateBlockRect;
  readonly glyph: string;
  readonly fill: string;
  readonly stroke: string | null;
  readonly glyphFill: string;
  readonly pinned: boolean;
}

/** Lopende sleep: startpunt + start-rect + de huidige (gesnapt/geclampte) rect. */
interface GateBlockDragState {
  readonly key: string;
  readonly pointerId: number;
  readonly startPointer: { x: number; y: number };
  readonly startRect: HdGateBlockRect;
  readonly currentRect: HdGateBlockRect;
  readonly moved: boolean;
}

/**
 * Overlay-svg met de mini-planeetblokjes, geprojecteerd in de `.svg-stage`
 * van `hd-bodygraph-chart` (ng-content). Zelfde viewBox als de chart-svg →
 * identieke letterbox, dus de viewBox-coördinaten van de layout vallen exact
 * op de chart, schalen mee en printen mee.
 *
 * Iteratie 2 voegt de verplaats-tool toe: met {@link moveToolActive} vangen de
 * blokjes pointer-drags om een handmatige pin te leggen (offsets relatief aan
 * het poort-anker). Zonder actieve tool blijft de laag zich als in iteratie 1
 * gedragen: enkel hover-integratie, geen sleep.
 */
@Component({
  selector: 'hd-gate-block-layer',
  templateUrl: './hd-gate-block-layer.component.html',
  styleUrl: './hd-gate-block-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.move-active]': 'moveToolActive()',
    '[class.dragging]': 'dragState() !== null',
  },
})
export class HdGateBlockLayerComponent {
  /** Al side-gefilterde activaties (`displayPlanetaryActivations` op de page). */
  readonly activations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly renderState = input.required<HdBodygraphRenderState>();
  /** Poort-ankers uit `hd-bodygraph-chart.gateAnchors()`; null tot de SVG geladen is. */
  readonly gateAnchors = input.required<ReadonlyMap<number, HdGateBlockRect> | null>();
  /** Vastgezette blokjes (offsets relatief aan het poort-anker). */
  readonly pins = input<readonly HdGateBlockPin[]>([]);

  /** Verplaats-tool (spec §8): alléén dan vangen de blokjes pointer-drags. */
  readonly moveToolActive = input(false);

  /** Sleep afgerond → pin (offsets relatief aan het poort-anker). */
  readonly pinCommitted = output<HdGateBlockPin>();
  /** Dubbelklik op een gepind blok → unpin. */
  readonly pinRemoved = output<Pick<HdGateBlockPin, 'gate' | 'planetId' | 'side'>>();

  private readonly svgRef = viewChild.required<ElementRef<SVGSVGElement>>('layerSvg');
  protected readonly dragState = signal<GateBlockDragState | null>(null);

  /**
   * Hover-integratie (optioneel, zoals bij de chart zelf): blokjes dimmen mee
   * met de chart-hover, en hover op een blokje publiceert een gate-hover zodat
   * de chart de bijbehorende poort highlight. Pauzeert zodra de verplaats-tool
   * actief is (spec §9): tijdens het slepen mag de chart niet mee-highlighten.
   */
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });

  constructor() {
    // Tool uit → een lopende sleep annuleren zonder commit (spec §8). `untracked`
    // zodat de effect enkel op `moveToolActive` reageert, niet op `dragState`.
    effect(() => {
      if (!this.moveToolActive()) {
        untracked(() => this.dragState.set(null));
      }
    });
  }

  protected isBlockDimmed(vm: GateBlockViewModel): boolean {
    // Tijdens de tool geen dim: de blokjes moeten allemaal zichtbaar/sleepbaar zijn.
    if (this.moveToolActive()) {
      return false;
    }
    const service = this.hoverService;
    // `dimsChart`: de blokjes liggen op de chart en volgen dus de chart-kant
    // (incl. "enkel één kant dimmen").
    if (!service || !service.isActive() || !service.dimsChart()) {
      return false;
    }
    return !service.isGateMatchingHover(vm.gate);
  }

  /**
   * "Dimmen bij hover" uit (app-setting): matchende blokjes krijgen een blauwe
   * rand (zie SCSS `.is-hover-outlined`) omdat ze zonder dim niet meer vanzelf
   * opvallen. Tijdens de verplaats-tool blijft dit uit, net als de dim.
   */
  protected isBlockOutlined(vm: GateBlockViewModel): boolean {
    if (this.moveToolActive()) {
      return false;
    }
    const service = this.hoverService;
    if (!service || !service.isActive() || service.dimOnHover()) {
      return false;
    }
    return service.isGateMatchingHover(vm.gate);
  }

  protected onBlockEnter(vm: GateBlockViewModel): void {
    // Geen gate-hover publiceren terwijl de tool actief is (spec §9).
    if (this.moveToolActive()) {
      return;
    }
    this.hoverService?.setGateHover(vm.gate, 'chart-gate');
  }

  protected onBlockLeave(): void {
    this.hoverService?.setGateHover(null, 'chart-gate');
  }

  protected readonly blockVms = computed<readonly GateBlockViewModel[]>(() => {
    const anchors = this.gateAnchors();
    if (!anchors || anchors.size === 0) {
      return [];
    }
    const descriptors = buildHdGateBlockDescriptors(this.activations(), this.renderState());
    const placements = computeHdGateBlockLayout({
      clusters: buildHdGateBlockClusters(descriptors),
      gateAnchors: anchors,
      channelSegments: buildHdGateBlockChannelSegments(anchors),
      pins: this.pins(),
    });
    return placements.map(placement => {
      const sideColor = HD_BODYGRAPH_COLORS[placement.descriptor.side];
      const defined = placement.descriptor.isDefined;
      return {
        key: hdGateBlockKey(placement.descriptor),
        gate: placement.descriptor.gate,
        planetId: placement.descriptor.planetId,
        side: placement.descriptor.side,
        rect: placement.rect,
        glyph: placement.descriptor.glyph,
        fill: defined ? sideColor : '#ffffff',
        stroke: defined ? null : sideColor,
        glyphFill: defined ? '#ffffff' : sideColor,
        pinned: placement.pinned,
      };
    });
  });

  /** blockVms + transiente sleep-rect; het gesleepte blok rendert als laatste (bovenop). */
  protected readonly displayBlockVms = computed<readonly GateBlockViewModel[]>(() => {
    const vms = this.blockVms();
    const drag = this.dragState();
    if (!drag) {
      return vms;
    }
    const dragged = vms.find(vm => vm.key === drag.key);
    if (!dragged) {
      return vms;
    }
    return [
      ...vms.filter(vm => vm.key !== drag.key),
      { ...dragged, rect: drag.currentRect },
    ];
  });

  /**
   * Scherm-pixel → chart-viewBox-coördinaat via de eigen svg-CTM. De overlay
   * heeft dezelfde viewBox als de chart en dekt exact dezelfde box, dus zijn
   * CTM volstaat (geen aparte chart-svg nodig zoals bij de annotatielaag).
   */
  private toViewBox(ev: PointerEvent): { x: number; y: number } {
    const ctm = this.svgRef().nativeElement.getScreenCTM();
    if (!ctm) {
      return { x: 0, y: 0 };
    }
    const pt = new DOMPoint(ev.clientX, ev.clientY).matrixTransform(ctm.inverse());
    return { x: pt.x, y: pt.y };
  }

  protected onBlockPointerDown(vm: GateBlockViewModel, ev: PointerEvent): void {
    if (!this.moveToolActive()) {
      return;
    }
    ev.preventDefault();
    (ev.target as Element).setPointerCapture?.(ev.pointerId);
    this.dragState.set({
      key: vm.key,
      pointerId: ev.pointerId,
      startPointer: this.toViewBox(ev),
      startRect: vm.rect,
      currentRect: vm.rect,
      moved: false,
    });
  }

  protected onLayerPointerMove(ev: PointerEvent): void {
    const drag = this.dragState();
    if (!drag || ev.pointerId !== drag.pointerId) {
      return;
    }
    const p = this.toViewBox(ev);
    const dx = p.x - drag.startPointer.x;
    const dy = p.y - drag.startPointer.y;
    const moved = drag.moved || Math.hypot(dx, dy) >= HD_GATE_BLOCK_DRAG_THRESHOLD;
    const raw: HdGateBlockRect = { ...drag.startRect, x: drag.startRect.x + dx, y: drag.startRect.y + dy };
    // Snap magnetisch aan de buurblokjes (het gesleepte blok zelf telt niet mee),
    // daarna binnen de viewBox houden.
    const neighbors = this.blockVms().filter(vm => vm.key !== drag.key).map(vm => vm.rect);
    const currentRect = clampHdGateBlockRectToViewBox(snapHdGateBlockRect(raw, neighbors));
    this.dragState.set({ ...drag, currentRect, moved });
  }

  protected onLayerPointerUp(ev: PointerEvent): void {
    const drag = this.dragState();
    if (!drag || ev.pointerId !== drag.pointerId) {
      return;
    }
    this.dragState.set(null);
    if (!drag.moved) {
      return; // klik ≠ pin: anders pint elke (dubbel)klik het blok op zijn auto-plek vast
    }
    const vm = this.blockVms().find(candidate => candidate.key === drag.key);
    const anchor = vm ? this.gateAnchors()?.get(vm.gate) : undefined;
    if (!vm || !anchor) {
      return;
    }
    this.pinCommitted.emit({
      gate: vm.gate,
      planetId: vm.planetId,
      side: vm.side,
      dx: drag.currentRect.x - anchor.x,
      dy: drag.currentRect.y - anchor.y,
    });
  }

  protected onBlockDblClick(vm: GateBlockViewModel): void {
    if (!this.moveToolActive() || !vm.pinned) {
      return;
    }
    this.pinRemoved.emit({ gate: vm.gate, planetId: vm.planetId, side: vm.side });
  }
}
